import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';

// Registration was reachable only through the Cognito Hosted UI: Login.tsx has
// always linked to /signup, but no route and no page existed, so the link fell
// through to the catch-all and bounced back to /login. The whole API surface
// (signUp, confirmSignUp, resendConfirmationCode) was already implemented in
// services/auth.ts and exposed on useAuth — only this was missing.
//
// Cognito answers a sign-up with CONFIRM_SIGN_UP, so this is two steps in one
// page rather than two routes: leaving the page between them would lose the
// email and password the confirmation needs.
type Step = 'details' | 'confirm';

export default function Signup() {
  const [step, setStep] = useState<Step>('details');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp, confirmSignUp, resendConfirmationCode, signIn } = useAuth();
  const navigate = useNavigate();

  const handleDetails = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    // Checked here only to fail fast with a readable message; the user pool
    // enforces its own policy and will reject anything weaker regardless.
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const result = await signUp(email, password, name || undefined);
      if (result.success) {
        setStep('confirm');
        toast.success('Check your email for the confirmation code');
      } else {
        toast.error(result.error || 'Failed to create account');
      }
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code) {
      toast.error('Enter the confirmation code');
      return;
    }

    setLoading(true);
    try {
      const result = await confirmSignUp(email, code);
      if (!result.success) {
        toast.error(result.error || 'Failed to confirm account');
        return;
      }
      // The password is still in state, so the account can be opened without
      // sending the user back to a login form to retype what they just chose.
      const signedIn = await signIn(email, password);
      if (signedIn.success) {
        toast.success('Welcome to Celsian AI');
        navigate('/dashboard');
      } else {
        toast.success('Account confirmed — please sign in');
        navigate('/login');
      }
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const result = await resendConfirmationCode(email);
    if (result.success) {
      toast.success('A new code is on its way');
    } else {
      toast.error(result.error || 'Could not resend the code');
    }
  };

  const inputClass =
    'appearance-none relative block w-full px-3 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600 sm:text-sm';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-primary-600 dark:text-primary-400">
          Celsian AI
        </p>
        <h1 className="mt-3 font-display text-4xl font-medium leading-tight text-gray-900 dark:text-gray-100">
          {step === 'details' ? 'Create your account.' : 'Confirm your email.'}
        </h1>
        <p className="mt-3 text-gray-600 dark:text-gray-400">
          {step === 'details'
            ? 'Build a chatbot, attach your own knowledge, and put it on your site.'
            : `We sent a code to ${email}.`}
        </p>

        <div className="mt-8 rounded-2xl border border-gray-300/70 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 shadow-[0_24px_70px_rgba(42,39,36,0.07)] p-8">
          {step === 'details' ? (
            <form className="space-y-5" onSubmit={handleDetails}>
              <div>
                <label htmlFor="name" className="block text-sm font-bold text-gray-900 dark:text-gray-100">
                  Name <span className="font-normal text-gray-500 dark:text-gray-400">optional</span>
                </label>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  className={`mt-2 ${inputClass}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-900 dark:text-gray-100">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`mt-2 ${inputClass}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-bold text-gray-900 dark:text-gray-100">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={`mt-2 ${inputClass}`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">At least 8 characters.</p>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current" />
                ) : (
                  'Continue'
                )}
              </button>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={handleConfirm}>
              <div>
                <label htmlFor="code" className="block text-sm font-bold text-gray-900 dark:text-gray-100">
                  Confirmation code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  className={`mt-2 ${inputClass}`}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current" />
                ) : (
                  'Confirm and continue'
                )}
              </button>
              <button
                type="button"
                onClick={handleResend}
                className="text-sm font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
              >
                Send the code again
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
